import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IntegrationFormValues } from './useIntegrationCard';
import FormInputText from '@ui/form/input/FormInputText';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import { IntegrationCredential, IntegrationCredentialForm } from '@app/model/integration-settings/Integration';
import { formatDate } from '../IntegrationSettings.utilities';

// A credential with named fields, from the provider registry (ROO2-296). Like the single value, nothing saved comes
// back from the API. "Replace" opens the fields empty; an empty field keeps its saved value.
const StructuredCredentialFields: React.FunctionComponent<{
	form: UseFormReturn<IntegrationFormValues>,
	providerLabel: string,
	credentialForm: IntegrationCredentialForm,
	credential: IntegrationCredential | null,
	isReplacing: boolean,
	onReplace: (replacing: boolean) => void,
}> = ({ form, providerLabel, credentialForm, credential, isReplacing, onReplace }) => {
	const isSaved = credential !== null && credential.saved;
	const shared = credentialForm.shared
		? <div className="sm:col-span-3">
			<Message state="info" title={`One ${providerLabel} credential for the whole company`}>
				Every hotel of this company uses the same credential. A change applies to all of them.
			</Message>
		</div>
		: null;
	if (isSaved && !isReplacing) {
		const savedBy = credential.savedBy === null ? '' : ` by ${credential.savedBy}`;
		return <>
			{shared}
			<div>
				<div className="flex items-end gap-x-2">
					<div className="min-w-0 flex-1">
						<FormInputText form={form} name="credentialMask" label={`${providerLabel} credential`} disabled />
					</div>
					<FormButton state="white" onClick={() => onReplace(true)}>Replace</FormButton>
				</div>
				<p className="help">{credential.savedAt === null
					? 'The company already holds this credential.'
					: `Saved on ${formatDate(credential.savedAt.slice(0, 10))}${savedBy}.`} RoomPulse never shows it.</p>
			</div>
		</>;
	}
	return <>
		{shared}
		{credentialForm.fields.map(field =>
			<FormInputText key={field.name} form={form} name={`credentialFields.${field.name}`} label={field.label}
				type={field.secret ? 'password' : 'text'} isRequired={!isSaved}
				placeholder={isSaved ? 'Leave empty to keep the saved value' : `From ${providerLabel}`} />)}
		<div className="sm:col-span-3">
			<div className="flex items-center gap-x-2">
				<p className="help flex-1">{isSaved ? 'The saved values stay until the test passes and you save.' : 'RoomPulse encrypts the credential at the save.'}</p>
				{isSaved && <FormButton state="text" onClick={() => {
					form.setValue('credentialFields', {}, { shouldDirty: true });
					onReplace(false);
				}}>Cancel</FormButton>}
			</div>
		</div>
	</>;
};

export default StructuredCredentialFields;
